import { Dec, SUPPORTED_CURRENCIES } from '@portal/contracts';

const ccyMeta = new Map(SUPPORTED_CURRENCIES.map((c) => [c.code, c]));
export const currencySymbol = (code: string) => ccyMeta.get(code)?.symbol ?? code;

/** "1234567.5" → "1,234,567.50" using string maths only — never floating point. */
export function groupDigits(value: string | number | null | undefined, dp = 2): string {
  if (value === null || value === undefined || value === '') return '—';
  let s: string;
  try {
    s = new Dec(value).toFixed(dp);
  } catch {
    return String(value);
  }
  const neg = s.startsWith('-');
  const [i = '0', f] = s.replace('-', '').split('.');
  return `${neg ? '−' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${f ? `.${f}` : ''}`;
}

/** Currency amount, e.g. "₦107,500.00". */
export function money(value: string | number | null | undefined, currency = 'NGN'): string {
  if (value === null || value === undefined || value === '') return '—';
  const sym = currencySymbol(currency);
  const body = groupDigits(value, 2);
  return `${sym}${sym.length > 1 && /[A-Za-z]$/.test(sym) ? ' ' : ''}${body}`;
}

export function qty(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return groupDigits(value, new Dec(value).decimalPlaces());
}

export function percent(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${new Dec(value).toFixed()}%`;
}

const dateFmt = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const monthFmt = new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' });

/** "2026-10-20" → "20 Oct 2026" (date-only strings are not shifted by timezone). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(d.getTime()) ? value : dateFmt.format(d);
}
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : dateTimeFmt.format(d);
}
export function formatMonth(ym: string): string {
  return monthFmt.format(new Date(`${ym}-01T00:00:00`));
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  const s = Math.round(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} day${d === 1 ? '' : 's'} ago`;
  return formatDate(value.slice(0, 10));
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

export function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function addDaysISO(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
